import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../api/client.js'
import { useNav } from '../nav/NavContext.js'

interface Change { field: string; old: unknown; new: unknown }
interface Entry {
  id: string
  created_at: string
  action: 'Insert' | 'Update' | 'SoftDelete' | 'Delete'
  table_name: string
  record_id: string | null
  actor: string
  impersonated: boolean
  changes: Change[]
}
interface Result { rows: Entry[]; total: number; page: number; pageSize: number }
interface Facets { tables: string[]; users: Array<{ id: string; name: string }> }

const ACTION_LABEL: Record<string, string> = { Insert: 'Created', Update: 'Changed', SoftDelete: 'Deactivated', Delete: 'Deleted' }
const niceTable = (t: string) => t.replace(/_/g, ' ')
const fmtDate = (iso: string) => new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
const fmtVal = (v: unknown): string => (v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v))

/**
 * Stage 12 §2.3: read-only trail. Rows expand to an old → new table rather than raw
 * JSON. There is no write path from this screen — the API has none either.
 */
export function AuditPage() {
  const { params } = useNav()
  const [facets, setFacets] = useState<Facets | null>(null)
  const [filters, setFilters] = useState({ user_id: '', table_name: params.table ?? '', record_id: params.record ?? '', action: '', from: '', to: '' })
  const [page, setPage] = useState(1)
  const [data, setData] = useState<Result | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    api.get<Facets>('/audit-log/facets').then(setFacets).catch(() => {})
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const q = new URLSearchParams({ page: String(page), pageSize: '50' })
    for (const [k, v] of Object.entries(filters)) if (v) q.set(k, v)
    try {
      setData(await api.get<Result>(`/audit-log?${q.toString()}`))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the audit trail')
    } finally {
      setLoading(false)
    }
  }, [filters, page])

  useEffect(() => {
    void load()
  }, [load])

  const set = (k: keyof typeof filters, v: string) => {
    setPage(1)
    setFilters((f) => ({ ...f, [k]: v }))
  }
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1

  return (
    <div>
      <h1 className="tf-login-title">Audit Trail</h1>
      <p className="tf-note">Every create, change and deactivation on business records. Views and reports are not logged. Entries cannot be edited or deleted.</p>

      <div className="tf-toolbar">
        <select value={filters.user_id} onChange={(e) => set('user_id', e.target.value)} aria-label="User">
          <option value="">All users</option>
          {facets?.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={filters.table_name} onChange={(e) => set('table_name', e.target.value)} aria-label="Record type">
          <option value="">All record types</option>
          {facets?.tables.map((t) => <option key={t} value={t}>{niceTable(t)}</option>)}
        </select>
        <select value={filters.action} onChange={(e) => set('action', e.target.value)} aria-label="Action">
          <option value="">All actions</option>
          {Object.entries(ACTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label>From <input type="date" value={filters.from} onChange={(e) => set('from', e.target.value)} /></label>
        <label>To <input type="date" value={filters.to} onChange={(e) => set('to', e.target.value)} /></label>
        {filters.record_id && (
          <span className="tf-pill">One record only <button type="button" className="tf-button-link" onClick={() => set('record_id', '')}>show all</button></span>
        )}
      </div>

      {error && <p className="tf-error">{error}</p>}
      <table className="tf-table">
        <thead>
          <tr><th>When (IST)</th><th>Who</th><th>What</th><th>Record type</th><th /></tr>
        </thead>
        <tbody>
          {loading && !data && <tr><td colSpan={5}>Loading…</td></tr>}
          {data && data.rows.length === 0 && <tr><td colSpan={5}>No entries match.</td></tr>}
          {data?.rows.map((r) => (
            <>
              <tr key={r.id}>
                <td>{fmtDate(r.created_at)}</td>
                <td>{r.actor}{r.impersonated ? ' (support session)' : ''}</td>
                <td>{ACTION_LABEL[r.action]}</td>
                <td>{niceTable(r.table_name)}</td>
                <td className="tf-row-actions"><button type="button" onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? 'Hide' : 'Details'}</button></td>
              </tr>
              {open === r.id && (
                <tr key={`${r.id}-d`}>
                  <td colSpan={5}>
                    {r.changes.length === 0 ? <span className="tf-note">No field values recorded.</span> : (
                      <table className="tf-diff">
                        <thead><tr><th>Field</th><th>Before</th><th>After</th></tr></thead>
                        <tbody>
                          {r.changes.map((c) => (
                            <tr key={c.field}>
                              <td>{c.field.replace(/_/g, ' ')}</td>
                              <td className={c.old !== null ? 'tf-old' : ''}>{fmtVal(c.old)}</td>
                              <td className={c.new !== null ? 'tf-new' : ''}>{fmtVal(c.new)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {r.record_id && !filters.record_id && (
                      <button type="button" className="tf-button-link" onClick={() => { setPage(1); setFilters((f) => ({ ...f, table_name: r.table_name, record_id: r.record_id! })) }}>
                        Show only this record's history
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>

      <div className="tf-pagination">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
        <span>Page {page} of {pages}{data ? ` · ${data.total} entries` : ''}</span>
        <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
      </div>
    </div>
  )
}
